/**
 * Meta click identifiers for Conversions API matching. `fbc` is built from the
 * arrival `fbclid` and kept in localStorage, not a cookie (WAF history: see
 * src/index.tsx); `fbp` and a fallback `_fbc` are read from Meta's cookies.
 */
const fbcStorageKey = "metaFbc";
const fbcLifetimeInMs = 90 * 24 * 60 * 60 * 1000;
const adClickPattern = /^[A-Za-z0-9_-]{1,512}$/;

interface KeyValueStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
}

export interface MetaClickIdentifiers {
  fbc?: string;
  fbp?: string;
}

export const buildFbc = (fbclid: string, timestampMs: number): string =>
  `fb.1.${timestampMs}.${fbclid}`;

const readCookie = (name: string, cookieString: string): string | undefined => {
  const prefix = `${name}=`;
  const cookie = cookieString.split("; ").find((candidate) => candidate.startsWith(prefix));
  return cookie?.slice(prefix.length) || undefined;
};

export const readFbp = (cookieString: string): string | undefined =>
  readCookie("_fbp", cookieString);

const readFbclid = (search: string): string | undefined => {
  const fbclid = new URLSearchParams(search).get("fbclid");
  return fbclid && adClickPattern.test(fbclid) ? fbclid : undefined;
};

const readStoredFbc = (storage: KeyValueStorage): string | undefined => {
  try {
    return storage.getItem(fbcStorageKey) ?? undefined;
  } catch {
    return undefined;
  }
};

const hasExpired = (fbc: string, nowMs: number): boolean => {
  const arrivalMs = Number(fbc.split(".")[2]);
  return Number.isNaN(arrivalMs) || nowMs - arrivalMs > fbcLifetimeInMs;
};

export const rememberFbc = ({
  search,
  storage,
  nowMs,
}: {
  search: string;
  storage: KeyValueStorage;
  nowMs: number;
}): void => {
  const fbclid = readFbclid(search);
  if (!fbclid) return;
  if (readStoredFbc(storage)?.endsWith(`.${fbclid}`)) return;

  try {
    storage.setItem(fbcStorageKey, buildFbc(fbclid, nowMs));
  } catch {
    // Blocked storage only loses the click ID; it must never break the flow.
  }
};

export const readMetaClickIdentifiers = ({
  storage,
  cookieString,
  nowMs,
}: {
  storage: KeyValueStorage;
  cookieString: string;
  nowMs: number;
}): MetaClickIdentifiers => {
  const fbc = [readStoredFbc(storage), readCookie("_fbc", cookieString)].find(
    (candidate) => candidate && !hasExpired(candidate, nowMs)
  );
  const fbp = readFbp(cookieString);
  return { ...(fbc && { fbc }), ...(fbp && { fbp }) };
};

const blockedStorage: KeyValueStorage = { getItem: () => null, setItem: () => undefined };

// Reaching window.localStorage can itself throw when site data is blocked;
// analytics must never break the page or the event that asked for identifiers.
const browserStorage = (): KeyValueStorage => {
  try {
    return window.localStorage;
  } catch {
    return blockedStorage;
  }
};

export const currentMetaClickIdentifiers = (): MetaClickIdentifiers => {
  try {
    return readMetaClickIdentifiers({
      storage: browserStorage(),
      cookieString: document.cookie,
      nowMs: Date.now(),
    });
  } catch {
    return {};
  }
};

export const rememberArrivalFbc = (): void =>
  rememberFbc({
    search: window.location.search,
    storage: browserStorage(),
    nowMs: Date.now(),
  });
